// 列选择器组件
// 提供字段选择、分组管理、快速配置等功能
import React, { useState, useMemo } from 'react';
import {
  Modal,
  Checkbox,
  Space,
  Button,
  Collapse,
  Typography,
  Tag,
  Tooltip,
  Row,
  Col,
  Input,
  Divider,
  message
} from 'antd';
import {
  SettingOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  SearchOutlined,
  ReloadOutlined,
  SaveOutlined,
  AppstoreOutlined,
  InfoCircleOutlined,
  BarChartOutlined,
  PercentageOutlined,
  DollarOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import { FieldConfig } from '../../types/field-mapping';
import { FIELD_GROUPS, DEFAULT_VISIBLE_COLUMNS } from '../../config/field-mapping';
import { FieldMappingUtils } from '../../utils/field-mapping-utils';

const { Panel } = Collapse;
const { Text } = Typography;
const { Search } = Input;

interface ColumnSelectorProps {
  visible: boolean;
  onClose: () => void;
  visibleColumns: string[];
  onVisibleColumnsChange: (columns: string[]) => void;
  onSave?: (columns: string[]) => void;
}

// 图标映射
const ICON_MAP: Record<string, React.ReactNode> = {
  InfoCircleOutlined: <InfoCircleOutlined />,
  AppstoreOutlined: <AppstoreOutlined />,
  BarChartOutlined: <BarChartOutlined />,
  PercentageOutlined: <PercentageOutlined />,
  DollarOutlined: <DollarOutlined />,
  ClockCircleOutlined: <ClockCircleOutlined />
};

// 预设配置
const PRESET_CONFIGS = [
  {
    key: 'basic',
    label: '基础视图',
    description: '显示最基本的信息',
    columns: ['factoryName', 'month', 'costType', 'productName', 'outputQuantity']
  },
  {
    key: 'cost_analysis',
    label: '成本分析',
    description: '专注于成本相关字段',
    columns: ['factoryName', 'productName', 'rawMaterialCost', 'laborCost', 'manufacturingCost', 'unitCostExcludingTax', 'profitPerTon']
  },
  {
    key: 'production',
    label: '生产数据',
    description: '生产相关的数量和比率',
    columns: ['factoryName', 'productName', 'outputQuantity', 'rawMaterialQuantity', 'defectQuantity', 'lossRate', 'yieldRate']
  },
  {
    key: 'comprehensive',
    label: '全面视图',
    description: '显示所有重要字段',
    columns: DEFAULT_VISIBLE_COLUMNS
  }
];

const ColumnSelector: React.FC<ColumnSelectorProps> = ({
  visible,
  onClose,
  visibleColumns,
  onVisibleColumnsChange,
  onSave
}) => {
  const [localVisibleColumns, setLocalVisibleColumns] = useState<string[]>(visibleColumns);
  const [searchText, setSearchText] = useState('');
  const [activeGroups, setActiveGroups] = useState<string[]>(
    Object.keys(FIELD_GROUPS).filter(key => FIELD_GROUPS[key].defaultExpanded)
  );

  // 按分组组织字段
  const fieldsByGroup = useMemo(() => {
    const groups: Record<string, FieldConfig[]> = {};
    
    Object.values(FIELD_GROUPS).forEach(group => {
      groups[group.key] = FieldMappingUtils.getFieldsByGroup(group.key);
    });
    
    return groups;
  }, []);

  // 搜索过滤
  const filteredFieldsByGroup = useMemo(() => {
    if (!searchText) return fieldsByGroup;
    
    const filtered: Record<string, FieldConfig[]> = {};
    
    Object.entries(fieldsByGroup).forEach(([groupKey, fields]) => {
      const filteredFields = fields.filter(field => 
        field.label.toLowerCase().includes(searchText.toLowerCase()) ||
        field.key.toLowerCase().includes(searchText.toLowerCase()) ||
        field.description?.toLowerCase().includes(searchText.toLowerCase())
      );
      
      if (filteredFields.length > 0) {
        filtered[groupKey] = filteredFields;
      }
    });
    
    return filtered;
  }, [fieldsByGroup, searchText]);

  // 统计信息
  const stats = useMemo(() => {
    const totalFields = Object.values(fieldsByGroup).flat().length;
    const visibleCount = localVisibleColumns.length;
    const hiddenCount = totalFields - visibleCount;
    
    return { totalFields, visibleCount, hiddenCount };
  }, [fieldsByGroup, localVisibleColumns]);

  // 处理字段选择
  const handleFieldToggle = (fieldKey: string, checked: boolean) => {
    const newColumns = checked
      ? [...localVisibleColumns, fieldKey]
      : localVisibleColumns.filter(key => key !== fieldKey);
    
    setLocalVisibleColumns(newColumns);
  };

  // 处理分组全选/取消全选
  const handleGroupToggle = (groupKey: string, checked: boolean) => {
    const groupFields = fieldsByGroup[groupKey].map(field => field.key);
    
    let newColumns: string[];
    if (checked) {
      // 添加该分组的所有字段
      newColumns = [...new Set([...localVisibleColumns, ...groupFields])];
    } else {
      // 移除该分组的所有字段
      newColumns = localVisibleColumns.filter(key => !groupFields.includes(key));
    }
    
    setLocalVisibleColumns(newColumns);
  };

  // 应用预设配置
  const applyPreset = (presetKey: string) => {
    const preset = PRESET_CONFIGS.find(p => p.key === presetKey);
    if (preset) {
      setLocalVisibleColumns(preset.columns);
      message.success(`已应用 ${preset.label} 配置`);
    }
  };

  // 重置为默认
  const resetToDefault = () => {
    setLocalVisibleColumns(DEFAULT_VISIBLE_COLUMNS);
    message.success('已重置为默认配置');
  };

  // 全选/取消全选
  const toggleAll = (checked: boolean) => {
    if (checked) {
      const allFields = Object.values(fieldsByGroup).flat().map(field => field.key);
      setLocalVisibleColumns(allFields);
    } else {
      setLocalVisibleColumns([]);
    }
  };

  // 保存配置
  const handleSave = () => {
    onVisibleColumnsChange(localVisibleColumns);
    onSave?.(localVisibleColumns);
    message.success(`已保存列配置，显示 ${localVisibleColumns.length} 个字段`);
    onClose();
  };

  // 取消
  const handleCancel = () => {
    setLocalVisibleColumns(visibleColumns);
    onClose();
  };

  return (
    <Modal
      title={
        <Space>
          <SettingOutlined />
          <span>列配置</span>
          <Tag color="blue">{stats.visibleCount}/{stats.totalFields}</Tag>
        </Space>
      }
      open={visible}
      onCancel={handleCancel}
      width={800}
      footer={
        <Space>
          <Button onClick={handleCancel}>取消</Button>
          <Button onClick={resetToDefault} icon={<ReloadOutlined />}>
            重置默认
          </Button>
          <Button type="primary" onClick={handleSave} icon={<SaveOutlined />}>
            保存配置
          </Button>
        </Space>
      }
    >
      {/* 顶部工具栏 */}
      <div style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={12}>
            <Search
              placeholder="搜索字段..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              prefix={<SearchOutlined />}
              allowClear
            />
          </Col>
          <Col span={12}>
            <Space>
              <Button
                size="small"
                onClick={() => toggleAll(true)}
                disabled={stats.visibleCount === stats.totalFields}
              >
                全选
              </Button>
              <Button
                size="small"
                onClick={() => toggleAll(false)}
                disabled={stats.visibleCount === 0}
              >
                清空
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      {/* 预设配置 */}
      <div style={{ marginBottom: 16 }}>
        <Text strong>快速配置：</Text>
        <div style={{ marginTop: 8 }}>
          <Space wrap>
            {PRESET_CONFIGS.map(preset => (
              <Button
                key={preset.key}
                size="small"
                onClick={() => applyPreset(preset.key)}
                type={localVisibleColumns.length === preset.columns.length && 
                      preset.columns.every(col => localVisibleColumns.includes(col)) ? 'primary' : 'default'}
              >
                <Tooltip title={preset.description}>
                  {preset.label}
                </Tooltip>
              </Button>
            ))}
          </Space>
        </div>
      </div>

      <Divider />

      {/* 字段分组 */}
      <Collapse
        activeKey={activeGroups}
        onChange={(keys) => setActiveGroups(keys as string[])}
        ghost
      >
        {Object.entries(filteredFieldsByGroup).map(([groupKey, fields]) => {
          const group = FIELD_GROUPS[groupKey];
          const groupFieldKeys = fields.map(f => f.key);
          const selectedInGroup = groupFieldKeys.filter(key => localVisibleColumns.includes(key));
          const isGroupFullySelected = selectedInGroup.length === groupFieldKeys.length;
          const isGroupPartiallySelected = selectedInGroup.length > 0 && selectedInGroup.length < groupFieldKeys.length;

          return (
            <Panel
              key={groupKey}
              header={
                <div onClick={(e) => e.stopPropagation()}>
                  <Space>
                    <Checkbox
                      checked={isGroupFullySelected}
                      indeterminate={isGroupPartiallySelected}
                      onChange={(e) => handleGroupToggle(groupKey, e.target.checked)}
                    />
                    {ICON_MAP[group.icon || 'InfoCircleOutlined']}
                    <Text strong style={{ color: group.color }}>
                      {group.label}
                    </Text>
                    <Tag color={group.color} style={{ marginLeft: 8 }}>
                      {selectedInGroup.length}/{groupFieldKeys.length}
                    </Tag>
                  </Space>
                  <div style={{ marginTop: 4 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {group.description}
                    </Text>
                  </div>
                </div>
              }
            >
              <Row gutter={[16, 8]}>
                {fields.map(field => (
                  <Col span={12} key={field.key}>
                    <div style={{
                      padding: '8px 12px',
                      border: '1px solid #f0f0f0',
                      borderRadius: 6,
                      backgroundColor: localVisibleColumns.includes(field.key) ? '#f6ffed' : '#fafafa'
                    }}>
                      <Space>
                        <Checkbox
                          checked={localVisibleColumns.includes(field.key)}
                          onChange={(e) => handleFieldToggle(field.key, e.target.checked)}
                        />
                        <div style={{ flex: 1 }}>
                          <div>
                            <Text strong>{field.label}</Text>
                            {field.required && (
                              <Text type="danger" style={{ marginLeft: 4 }}>*</Text>
                            )}
                          </div>
                          <div>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {field.description}
                            </Text>
                          </div>
                          <div style={{ marginTop: 4 }}>
                            <Space size={4}>
                              <Tag color="default">{field.type}</Tag>
                              {field.sortable && <Tag color="blue">可排序</Tag>}
                              {field.filterable && <Tag color="green">可筛选</Tag>}
                              {field.searchable && <Tag color="orange">可搜索</Tag>}
                            </Space>
                          </div>
                        </div>
                      </Space>
                    </div>
                  </Col>
                ))}
              </Row>
            </Panel>
          );
        })}
      </Collapse>

      {/* 底部统计 */}
      <div style={{
        marginTop: 16,
        padding: '12px 16px',
        backgroundColor: '#fafafa',
        borderRadius: 6,
        textAlign: 'center'
      }}>
        <Space>
          <Text>
            <EyeOutlined style={{ color: '#52c41a' }} /> 已选择: {stats.visibleCount}
          </Text>
          <Divider type="vertical" />
          <Text>
            <EyeInvisibleOutlined style={{ color: '#999' }} /> 已隐藏: {stats.hiddenCount}
          </Text>
          <Divider type="vertical" />
          <Text>总计: {stats.totalFields} 个字段</Text>
        </Space>
      </div>
    </Modal>
  );
};

export default ColumnSelector;
